 // @ts-nocheck

import { DOMOutputSpec, Schema } from "prosemirror-model";
import { MarkdownParser, MarkdownSerializer } from "prosemirror-markdown";
import { Decoration, DecorationSet } from "prosemirror-view";
import { Plugin } from "prosemirror-state";
import markdownit from "markdown-it/lib";
import { RegexConstants } from "Constants";

// ::Schema Document schema for the data model used by CommonMark.
export const findestSchema = new Schema({
    nodes: {
        doc: {
            content: "block+"
        },

        paragraph: {
            content: "inline*",
            group: "block",
            parseDOM: [{ tag: "p" }],
            toDOM() { return ["p", 0]; }
        },

        blockquote: {
            content: "block+",
            group: "block",
            parseDOM: [{ tag: "blockquote" }],
            toDOM() { return ["blockquote", 0]; }
        },

        horizontal_rule: {
            group: "block",
            parseDOM: [{ tag: "hr" }],
            toDOM() { return ["div", ["hr"]]; }
        },

        heading: {
            content: "inline*",
            group: "block",
            parseDOM: [{ tag: "p" }],
            toDOM() { return ["p", 0]; }
        },

        code_block: {
            content: "inline*",
            group: "block",
            parseDOM: [{ tag: "p" }],
            toDOM() { return ["p", 0]; }
        },

        ordered_list: {
            content: "list_item+",
            group: "block",
            attrs: { order: { default: 1 }, tight: { default: false } },
            parseDOM: [{
                tag: "ol", getAttrs(dom) {
                    const newDom = dom as Element;

                    return {
                        order: newDom.hasAttribute("start") ? +newDom.getAttribute("start")! : 1,
                        tight: newDom.hasAttribute("data-tight")
                    };
                }
            }],
            toDOM(node) {
                return ["ol", {
                    start: node.attrs.order === 1 ? null : node.attrs.order,
                    "data-tight": node.attrs.tight ? "true" : null
                }, 0] as DOMOutputSpec;
            }
        },

        bullet_list: {
            content: "list_item+",
            group: "block",
            attrs: { tight: { default: false } },
            parseDOM: [{ tag: "ul", getAttrs(dom) {
                const newDom = dom as Element;
                return {
                    tight: newDom.hasAttribute("data-tight")
                };
            }}],
            toDOM(node) { return ["ul", { "data-tight": node.attrs.tight ? "true" : null }, 0] as DOMOutputSpec; }
        },

        list_item: {
            content: "paragraph block*",
            defining: true,
            parseDOM: [{ tag: "li" }],
            toDOM() { return ["li", 0]; }
        },

        text: {
            group: "inline",
            toDOM(node) { return node.text as DOMOutputSpec;}
        },

        hard_break: {
            inline: true,
            group: "inline",
            selectable: false,
            parseDOM: [{ tag: "br" }],
            toDOM() { return ["br"]; }
        }
    },

    marks: {
        link: {
            attrs: {
                href: {},
                title: { default: null },
                rel: { default: "noreferrer noopener" }
            },
            inclusive: true,
            parseDOM: [{
                tag: "a[href]", getAttrs(dom) {
                    const newDom = dom as Element;
                    return { href: newDom.getAttribute("href"), title: null };
                }
            }],
            toDOM(node) { return ["a", node.attrs]; }
        },

        em: {
            parseDOM: [{ tag: "i" }, { tag: "em" },
            { style: "font-style", getAttrs: value => value === "italic" && null }],
            toDOM() { return ["em"]; }
        },

        strong: {
            parseDOM: [{ tag: "b" }, { tag: "strong" },
            { style: "font-weight", getAttrs: value => {
                const newValue = value as string;
                return RegexConstants.PROSEMIRROR_SCHEMA_BOLD_REGEX.test(newValue) && null; }}],
            toDOM() { return ["strong"]; }
        },

        subscript: {
            parseDOM: [{ tag: "sub"} ],
            toDOM() { return ["sub"]; }
        },

        superscript: {
            parseDOM: [{ tag: "sup" }],
            toDOM() { return ["sup"]; }
        },

        code: {
            parseDOM: [{ tag: "code" }],
            toDOM() { return ["p"]; }
        },

        marked: {
            parseDOM: [{ tag: "mark" }],
            toDOM() { return ["mark"]; }
        }
    }
});

// :: MarkdownSerializer
// A serializer for the [basic schema](#schema).
export const findestMarkdownSerializer = new MarkdownSerializer({
    blockquote(state, node) {
        state.wrapBlock("> ", null, node, () => state.renderContent(node));
    },
    code_block(state, node) {
        state.renderInline(node);
        state.closeBlock(node);
    },
    heading(state, node) {
        state.renderInline(node);
        state.closeBlock(node);
    },
    horizontal_rule(state, node) {
        state.write(node.attrs.markup || "---");
        state.closeBlock(node);
    },
    bullet_list(state, node) {
        state.renderList(node, "  ", () => (node.attrs.bullet || "*") + " ");
    },
    ordered_list(state, node) {
        const start = node.attrs.order || 1;
        const maxW = String(start + node.childCount - 1).length;
        const space = state.repeat(" ", maxW + 2);
        state.renderList(node, space, i => {
            const nStr = String(start + i);
            return state.repeat(" ", maxW - nStr.length) + nStr + ". ";
        });
    },
    list_item(state, node) {
        state.renderContent(node);
    },
    paragraph(state, node) {
        state.renderInline(node);
        state.closeBlock(node);
    },

    hard_break(state, node, parent, index) {
        for (let i = index; i < parent.childCount; i++) {
            if (parent.child(i).type !== node.type) {
                state.write("\\\n");
                return;
            }
        }
    },
    text(state, node: any) {
        state.text(node.text);
    }
}, {
        em: { open: "*", close: "*", mixable: true, expelEnclosingWhitespace: true },
        strong: { open: "**", close: "**", mixable: true, expelEnclosingWhitespace: true },
        link: {
            open(_state, mark: any, parent, index) {
                return isPlainURL(mark, parent, index, 1) ? "<" : "[";
            },
            close(state, mark: any, parent, index) {
                return isPlainURL(mark, parent, index, -1) ? ">"
                    : "](" + state.esc(mark.attrs.href) +
                    (mark.attrs.title ? " " + state.quote(mark.attrs.title) : "") + ")";
            }
        },
        subscript: { open: "~", close: "~", mixable: true, expelEnclosingWhitespace: true },
        superscript: { open: "^", close: "^", mixable: true, expelEnclosingWhitespace: true },
        code: {
            open: "",
            close: "",
            escape: true
        },
        marked: { open: "==", close: "==", mixable: true, expelEnclosingWhitespace: true},
    });

export const findestMarkdownParser = new MarkdownParser(findestSchema,
        markdownit("commonmark", { html: false })
        .use(require("markdown-it-sub")).use(require("markdown-it-sup")).use(require("markdown-it-mark")), {

    blockquote: { block: "blockquote" },
    paragraph: { block: "paragraph" },
    list_item: { block: "list_item" },
    bullet_list: { block: "bullet_list" },
    ordered_list: { block: "ordered_list", getAttrs: tok => {
        const attrOrder: string | null = tok.attrGet("order");
        let newOrder = 1;
        if (attrOrder) {
            newOrder = parseInt(attrOrder, 10);
        }
        return ({ order: newOrder});
    }},
    heading: { block: "heading" },
    code_block: { block: "code_block" },
    hr: { node: "horizontal_rule" },
    hardbreak: { node: "hard_break" },
    link: {
        mark: "link", getAttrs: tok => ({
            href: tok.attrGet("href"),
            title: null,
            rel: "noreferrer noopener"
        })
    },
    em: { mark: "em" },
    strong: { mark: "strong" },
    sub: { mark: "subscript" },
    sup: { mark: "superscript" },
    code_inline: { mark: "code" },
    mark: { mark: "marked"}
});

export const placeholderPlugin = new Plugin({
    props: {
        decorations: state => {
            const decorations: Decoration[] = [];

            const decorate = (node: any, pos: number) => {
                if (node.type.isBlock && node.childCount === 0) {
                    decorations.push(
                        Decoration.node(pos, pos + node.nodeSize, {
                            class: "empty-node"
                        })
                    );
                }
            };

            state.doc.descendants(decorate);
            return DecorationSet.create(state.doc, decorations);
        }
    }
});


export function markApplies(doc: any, ranges: any, type: any) {
    for (let i = 0; i < ranges.length; i++) {
        const { $from, $to } = ranges[i];
        let can = $from.depth === 0 ? doc.type.allowsMarkType(type) : false;
        doc.nodesBetween($from.pos, $to.pos, (node: any) => {
            if (can) return false;
            can = node.inlineContent && node.type.allowsMarkType(type);
        });
        if (can) return true;
    }
    return false;
}

export function stopOnTypes(markTypes: any) {
    return function (state: any, dispatch: any) {
        let selectionParent = state.selection.$from.parent;
        let fromPos = state.selection.$from.parentOffset;
        let toPos = state.selection.$to.parentOffset;
        const checkRange = fromPos !== toPos;

        let hasOneType = false;

        for (let i = 0; i < markTypes.length; i++) {
            if (checkRange) {
                hasOneType = hasOneType || (
                    selectionParent.rangeHasMark(fromPos, fromPos + 1, markTypes[i]) &&
                    selectionParent.rangeHasMark(toPos - 1, toPos, markTypes[i])
                );
            }

            if (state.storedMarks !== null && state.storedMarks !== undefined) {
                for (let j = 0; j < state.storedMarks.length; j++) {
                    const storedMark = state.storedMarks[j];
                    hasOneType = hasOneType || storedMark.type.name === markTypes[i].name;
                }
            }
        }


        return hasOneType;
    };
}

export function goOnOnTypes(markTypes: any) {
    return function (state: any, dispatch: any) {
        let selectionParent = state.selection.$from.parent;
        let fromPos = state.selection.$from.parentOffset - 1;
        let toPos = state.selection.$to.parentOffset;

        let hasOneType = false;

        for (let i = 0; i < markTypes.length; i++) {
            hasOneType = hasOneType || (
                selectionParent.rangeHasMark(fromPos, fromPos + 1, markTypes[i]) &&
                selectionParent.rangeHasMark(toPos - 1, toPos, markTypes[i])
            );
        }

        return !hasOneType;
    };
}

// TODO: maybe use markApplies in site.js isBold code
export function removeMarkSelection(markType: any) {
    return function (state: any, dispatch: any) {
        const { empty, $cursor, ranges } = state.selection;
        if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) return false;
        if (dispatch) {
            if ($cursor) {
                if (markType.isInSet(state.storedMarks || $cursor.marks())) {
                    dispatch(state.tr.removeStoredMark(markType));
                }
            }
        }
        return false;
    };
}

function isPlainURL(link: string, parent: any, index: number, side: number) {
  return false;
}

export function findestEscape(str: string, startOfLine: boolean) {
    str = str.replace(RegexConstants.PROSEMIRROR_FIND_ESCAPED_SQUARE_BRACKET, RegexConstants.PROSEMIRROR_ESCAPED_REPLACE);
    str = str.replace(RegexConstants.PROSEMIRROR_FIND_EQUALS, RegexConstants.PROSEMIRROR_ESCAPED_REPLACE);
    if (startOfLine) { str = str.replace(RegexConstants.PROSEMIRROR_FIND_ANCHOR_REFERENCES, RegexConstants.PROSEMIRROR_ESCAPED_REPLACE).replace(RegexConstants.PROSEMIRROR_FIND_DIGITS, "$1\\."); }
    return str;
}